// Performance optimization utilities
import { supabase } from "@/integrations/supabase/client";

// Debounced fetch to prevent duplicate requests
const requestCache = new Map<string, Promise<any>>();
const REQUEST_TIMEOUT = 5000; // 5 second cache

export const optimizedFetch = async <T>(
  cacheKey: string,
  fetcher: () => Promise<T>
): Promise<T> => {
  // Return cached promise if it exists
  if (requestCache.has(cacheKey)) {
    return requestCache.get(cacheKey);
  }

  // Create new request
  const promise = fetcher();
  requestCache.set(cacheKey, promise);

  // Clear cache after timeout
  setTimeout(() => {
    requestCache.delete(cacheKey);
  }, REQUEST_TIMEOUT);

  return promise;
};

// Optimized query builder for minimal data fetching
export const createOptimizedQuery = (
  tableName: string,
  fields: string[],
  filters?: Record<string, any>,
  orderBy?: { column: string; ascending?: boolean },
  limit?: number
) => {
  let query = supabase
    .from(tableName)
    .select(fields.join(','));

  // Apply filters
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.eq(key, value);
      }
    });
  }

  // Apply ordering
  if (orderBy) {
    query = query.order(orderBy.column, { 
      ascending: orderBy.ascending ?? false 
    });
  }

  // Apply limit
  if (limit) {
    query = query.limit(limit);
  }

  return query;
};

// Bundle preloading for critical routes
export const preloadCriticalRoutes = () => {
  // Preload most used admin routes
  const criticalImports = [
    () => import("@/pages/admin/Users"),
    () => import("@/pages/admin/Cultivars"),
    () => import("@/pages/Marketplace/Browse")
  ];

  // Preload in idle time
  if ('requestIdleCallback' in window) {
    criticalImports.forEach(importFn => {
      requestIdleCallback(() => importFn());
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      criticalImports.forEach(importFn => importFn());
    }, 1000);
  }
};

// Memory cleanup
export const cleanupMemory = () => {
  requestCache.clear();
  
  // Force garbage collection if available (dev mode)
  if (process.env.NODE_ENV === 'development' && 'gc' in window) {
    (window as any).gc();
  }
};